import { ActionButton, Dialog, IContextualMenuItem, IContextualMenuProps, Spinner, SpinnerSize } from '@fluentui/react';
import { useRequestReassign } from 'app/services/request-api';
import { IReassignRequestDto, RequestDomain, RequestDto, RequestType } from 'generated/request-backend';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppQueryClient } from 'app/services/clients/query-client';
import { useBoolean } from '@fluentui/react-hooks';
import moment from 'moment';
import { StringConstants } from 'app/utils/constants';
import { GetTypes } from 'app/components/helpers';

interface IProps {
    selectedRequest?: RequestDto;
    onRefetchHandler?: Function;
}

const RequestTypeDropdown: React.FC<IProps> = (props) => {
    const { t: translate } = useTranslation();
    const { selectedRequest, onRefetchHandler } = props;

    const [requestTypeSelection, setRequestTypeSelection] = useState(0);
    const [hideLoaderDialog, { toggle: toggleLoaderHideDialog }] = useBoolean(true);

    const {
        mutate: requestReassign,
        isLoading: isReassigning,
        isSuccess: isSuccessRequestReassign,
        isError: isErrorRequestReassign
    } = useRequestReassign();

    const handleRequestTypeSelection = (ev?: React.MouseEvent<HTMLElement>, item?: IContextualMenuItem): void => {
        if (parseInt(item.key) === selectedRequest.requestType) {
            ev.preventDefault();
            return;
        } else {
            let reassignmentData : IReassignRequestDto = {
                currentTeam: selectedRequest.requestTeam,
                currentRequestType: selectedRequest.requestType,
                reassignTeam: selectedRequest.requestTeam,
                reassignRequestType: parseInt(item.key),
                requestId: selectedRequest.unifiedTrackingId,
                requestedResolutionDate: moment(selectedRequest.requestedResolutionDate).isValid()
                    ? new Date(moment(selectedRequest.requestedResolutionDate).toISOString())
                    : null
            };
            requestReassign(reassignmentData)
        }
    };

    const sdsgMenu: IContextualMenuProps = React.useMemo(
        () => ({
            shouldFocusOnMount: true,
            className: 'admin-edit-status-menu',
            items: [
                {
                    key: "1",
                    text: translate('Proposal'),
                    canCheck: true,
                    isChecked: requestTypeSelection === 1 ? true : false,
                    onClick: handleRequestTypeSelection
                } as IContextualMenuItem,
                {
                    key: "2",
                    text: translate('Question'),
                    canCheck: true,
                    isChecked: requestTypeSelection === 2 ? true : false,
                    onClick: handleRequestTypeSelection
                } as IContextualMenuItem,
                {
                    key: "3",
                    text: 'Rfx',
                    canCheck: true,
                    isChecked: requestTypeSelection === 3 ? true : false,
                    onClick: handleRequestTypeSelection
                } as IContextualMenuItem
            ]
        }),
        [handleRequestTypeSelection, requestTypeSelection]
    );

    const cxpMenu: IContextualMenuProps = React.useMemo(
        () => ({
            shouldFocusOnMount: true,
            className: 'admin-edit-status-menu',
            items: [
                {
                    key: "8",
                    text: translate('Variant'),
                    canCheck: true,
                    isChecked: requestTypeSelection === 8 ? true : false,
                    onClick: handleRequestTypeSelection
                } as IContextualMenuItem,
                {
                    key: "9",
                    text: translate('PFR'),
                    canCheck: true,
                    isChecked: requestTypeSelection === 9 ? true : false,
                    onClick: handleRequestTypeSelection
                } as IContextualMenuItem,
                {
                    key: "10",
                    text: translate('Messaging'),
                    canCheck: true,
                    isChecked: requestTypeSelection === 10 ? true : false,
                    onClick: handleRequestTypeSelection
                } as IContextualMenuItem,
                {
                    key: "11",
                    text: translate('Technical'),
                    canCheck: true,
                    isChecked: requestTypeSelection === 11 ? true : false,
                    onClick: handleRequestTypeSelection
                } as IContextualMenuItem,
                {
                    key: "12",
                    text: translate('Program'),
                    canCheck: true,
                    isChecked: requestTypeSelection === 12 ? true : false,
                    onClick: handleRequestTypeSelection
                } as IContextualMenuItem
            ]
        }),
        [handleRequestTypeSelection, requestTypeSelection]
    );

    const cssMenu: IContextualMenuProps = React.useMemo(
        () => ({
            shouldFocusOnMount: true,
            className: 'admin-edit-status-menu',
            items: [
                {
                    key: "6",
                    text: translate('CSS Request'),
                    canCheck: true,
                    isChecked: requestTypeSelection === 6 ? true : false,
                    onClick: handleRequestTypeSelection
                } as IContextualMenuItem,
                {
                    key: "7",
                    text: translate(StringConstants.REVIEW_REQUEST),
                    canCheck: true,
                    isChecked: requestTypeSelection === 7 ? true : false,
                    onClick: handleRequestTypeSelection
                } as IContextualMenuItem
            ]
        }),
        [handleRequestTypeSelection, requestTypeSelection]
    );

    const crewMenu: IContextualMenuProps = React.useMemo(
        () => ({
            shouldFocusOnMount: true,
            className: 'admin-edit-status-menu',
            items: [
                {
                    key: "4",
                    text: translate('Pre Sales'),
                    canCheck: true,
                    isChecked: requestTypeSelection === 4 ? true : false,
                    onClick: handleRequestTypeSelection
                } as IContextualMenuItem,
                {
                    key: "5",
                    text: translate(StringConstants.POST_SALES),
                    canCheck: true,
                    isChecked: requestTypeSelection === 5 ? true : false,
                    onClick: handleRequestTypeSelection
                } as IContextualMenuItem,
                {
                    key: "13",
                    text: translate('Warranty'),
                    canCheck: true,
                    isChecked: requestTypeSelection === 13 ? true : false,
                    onClick: handleRequestTypeSelection
                } as IContextualMenuItem,
                {
                    key: "14",
                    text: translate('Product'),
                    canCheck: true,
                    isChecked: requestTypeSelection === 14 ? true : false,
                    onClick: handleRequestTypeSelection
                } as IContextualMenuItem,
                {
                    key: "15",
                    text: translate('Messaging'),
                    canCheck: true,
                    isChecked: requestTypeSelection === 15 ? true : false,
                    onClick: handleRequestTypeSelection
                } as IContextualMenuItem
            ]
        }),
        [handleRequestTypeSelection, requestTypeSelection]
    );

    const menuType: IContextualMenuProps = React.useMemo(() => {
        switch (selectedRequest.requestTeam) {
            // SDSG
            case RequestDomain.SDSG:
                return sdsgMenu;
            // CXP
            case RequestDomain.CXP:
                return cxpMenu;
            // CSS
            case RequestDomain.CSS:
                return cssMenu;
            // CREW
            case RequestDomain.Crew:
                return crewMenu;

            default:
                break;
        }
    }, [selectedRequest.requestTeam, requestTypeSelection, selectedRequest.requestType, selectedRequest]);

    useEffect(() => {
        setRequestTypeSelection(selectedRequest?.requestType);
    }, [selectedRequest?.requestType]);

    useEffect(() => {
        if (isReassigning) {
            toggleLoaderHideDialog();
        }

        if (!isReassigning && isSuccessRequestReassign) {
            // setTimeout(() => {
            //     AppQueryClient.invalidateQueries('requestQuery').then(() => toggleLoaderHideDialog());
            //     AppQueryClient.invalidateQueries('getRequestById');
            //     if (onRefetchHandler) {
            //         onRefetchHandler();
            //     }
            // }, 2000);
            AppQueryClient.invalidateQueries('requestQuery').then(() => toggleLoaderHideDialog());
            AppQueryClient.invalidateQueries('getRequestById');
            if (onRefetchHandler) {
                onRefetchHandler();
            }
        }
    }, [isReassigning, isSuccessRequestReassign, isErrorRequestReassign]);

    return (
        <>
            <div>
                {selectedRequest?.requestType && (
                    <ActionButton
                        menuProps={menuType}
                        className="status-selection-menu"
                        data-testid="request-type-selection-menu-test">
                        {GetTypes(selectedRequest.requestType)}
                    </ActionButton>
                )}
            </div>

            <Dialog hidden={hideLoaderDialog} onDismiss={toggleLoaderHideDialog}>
                <Spinner size={SpinnerSize.large} label="Updating request type. Please wait..." />
            </Dialog>
        </>
    );
};

export default RequestTypeDropdown;
